import React, { useEffect, useCallback, useRef } from 'react'
import tw, { styled } from 'twin.macro'
import { useStateContext } from '../context/stateContext'
import { motion } from 'framer-motion'
import useWindowSize from './../hooks/useWindowSize'
import content from './../data/content.json'
import parse from 'html-react-parser'
import Sound from './Sound'
import { Howl } from 'howler'
import transitionSound from '../assets/sounds/sound_transition.wav'
import jingleBellsSound from '../assets/sounds/sound_jinglebells.wav'
import christmasTreeSound from '../assets/sounds/sound_christmastree.wav'
import silentNightSound from '../assets/sounds/sound_silentnight.wav'
import introCat from '../assets/intro-cat.png'

const Container = styled(motion.div)`
	${tw`fixed top-0 bottom-0 w-full h-full overflow-hidden`}
`
const Wrap = styled.div`
	${tw`relative flex flex-col-reverse justify-center h-full md:flex-row`}
`
const Overline = styled.p`
	${tw`mb-5 text-2xl font-bold text-yellow md:text-6xl`}
`
const Description = styled.p`
	${tw`mb-5 text-sm font-normal text-white md:text-xl xs:mb-8`}
`
const IntroCat = styled.img`
	${tw`absolute bottom-0 left-0 right-0 mx-auto z-[1] pointer-events-none`}
`

const Main = styled(motion.div)`
	${tw`relative flex flex-col justify-center w-full py-10 text-center h-1/2 md:h-auto md:py-0`}

	& .content {
		${tw`relative z-10 w-full px-5 mx-auto text-center md:px-0 md:pl-10 xl:pl-20 xs:w-11/12 sm:w-3/5`}
	}

	& .legals {
		${tw`absolute w-full text-center transform -translate-x-1/2 md:w-3/5 left-1/2 bottom-3 md:bottom-10 text-9px xs:text-xs `}

		a:first-of-type {
			${tw`px-4 border-r border-black md:px-0 md:pr-4`}
		}

		a:not(:first-of-type) {
			${tw`px-4 `}
		}
	}
`

const Welcome = () => {
	const stateContext = useStateContext()
	const { playSound } = useStateContext()
	const [playOnce, setPlayOnce] = React.useState(false)
	const { locale } = stateContext.state
	const t = content.langs
	const { isMobile } = useWindowSize()
	const [entrySound, setEntrySound] = React.useState(null)

	const onStartGameClick = useCallback(() => {
		stateContext.dispatch({ action: 'SET_GAME_STATUS', data: 'gameStart' })
		if (stateContext.state.sound) {
			playSound('transition')

			entrySound.stop()
		}
	}, [stateContext, entrySound]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (stateContext.state.sounds.transition === null) {
			const newSound = new Howl({
				src: [transitionSound],
				volume: 0.5,
			})

			stateContext.dispatch({ action: 'SET_SOUND_FILE', data: 'transition', src: newSound })
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (entrySound === null) {
			const startSounds = [jingleBellsSound, christmasTreeSound, silentNightSound]

			const randomIndex = Math.floor(Math.random() * startSounds.length)

			const newSound = new Howl({
				src: [startSounds[randomIndex]],
				volume: 0.5,
			})
			stateContext.dispatch({ action: 'SET_SOUND_FILE', data: 'entry_sound', src: newSound })
			setEntrySound(newSound)
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!playOnce && entrySound && stateContext.state.sound) {
			setPlayOnce(true)
			if (stateContext.state.sound) {
				playSound('entry_sound')
			}
		}
	}, [entrySound, playOnce, stateContext])

	const mainVariants = {
		init: {
			y: isMobile ? '100%' : '-100%',
		},
		animate: {
			y: '0%',
			transition: {
				duration: 0.7,
			},
		},
		exit: {
			y: isMobile ? '100%' : '-100%',
			transition: {
				duration: 0.4,
			},
		},
	}

	return (
		<Container>
			<Sound mode='home' mobile={true} />
			<Wrap>
				<Main variants={mainVariants} initial='init' animate='animate' exit='exit'>
					<Sound mode='home' />
					<div className='content'>
						<Overline>Jetzt Memo spielen und gewinnen! </Overline>
						<Description>{parse(t[locale].description)}</Description>
						<button className='bg-pink text-white md:text-lg py-2 md:py-4 px-4 uppercase md:tracking-wider min-w-[180px] md:min-w-[240px]' onClick={() => onStartGameClick()}>
							{t[locale].buttonStart}
						</button>
					</div>
				</Main>
				<IntroCat src={introCat} alt='' />
			</Wrap>
		</Container>
	)
}

export default Welcome
